import {useMemo} from "react";
import {useSelector} from "react-redux";
import {useQuery} from "react-query";
import type {AgeCategory} from "crqlar-react-common/dist/models/ageCategory/ageCategory.model";
import type {Limit} from "crqlar-react-common/dist/models/common/limit";
import type {User} from "crqlar-react-common/dist/models/identity/user.model";
import {
    Printout,
    PrintoutType,
} from "crqlar-react-common/dist/models/printout/printout.model";
import type {Profile} from "crqlar-react-common/dist/models/profile/profile.model";
import {
    Reservation,
    ReservationStatus,
} from "crqlar-react-common/dist/models/reservation/reservation.model";
import type {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {Sector, SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import type {Service} from "crqlar-react-common/dist/models/service/service.model";
import {AgeCategoryService} from "crqlar-react-common/dist/services/ageCategory/ageCategory.service";
import {PrintoutService} from "crqlar-react-common/dist/services/printout/printout.service";
import {ProfileService} from "crqlar-react-common/dist/services/profile/profile.service";
import {ReservationService} from "crqlar-react-common/dist/services/reservation/reservation.service";
import {ResourceService} from "crqlar-react-common/dist/services/resource/resource.service";
import {SectorService} from "crqlar-react-common/dist/services/sector/sector.service";
import {ServiceService} from "crqlar-react-common/dist/services/service/service.service";
import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {
    convertLocalDateToDate,
    endOfDay,
    startOfDay,
} from "crqlar-react-common/dist/utils/date.util";
import {printoutHasLanguage, printoutHasLayouts, resolvePrintoutLanguage} from "../../../core/printoutDocument";
import type {PrintoutVariableResolveContext} from "../../../variables/printoutVariableResolver";

type ResolveRecord = Record<string, unknown>;

export interface AgendaPrintItem extends ResolveRecord {
    id?: string;
    start?: Date;
    end?: Date;
    sectorType?: SectorType;
    sector?: ResolveRecord;
    service?: ResolveRecord;
    reservation?: ResolveRecord;
    resources?: ResolveRecord[];
}

export interface AgendaPrintData {
    agendaItems: AgendaPrintItem[];
    context?: PrintoutVariableResolveContext;
    errorMessage?: string;
    isLoading: boolean;
    language?: string;
    profile?: Profile;
    rangeStart: Date;
    rangeEnd: Date;
    accommodationReservation?: Reservation;
    selectedPrintout?: Printout;
    printouts: Printout[];
}

const LIST_LIMIT: Limit = {
    offset: 0,
    limit: 1000,
    sort: [{field: "name", direction: "ASC"}],
};

const PRINTOUT_LIST_LIMIT: Limit = {
    offset: 0,
    limit: 1000,
    sort: [{field: "name", direction: "ASC"}],
};

const RESERVATION_LIMIT: Limit = {
    offset: 0,
    limit: 300,
    sort: [{field: "expectedStartDate", direction: "ASC"}],
};

const AGENDA_STATUSES: ReservationStatus[] = [
    ReservationStatus.CONFIRMED,
    ReservationStatus.PENDING,
    ReservationStatus.ACTIVE,
    ReservationStatus.COMPLETED,
];

const AGENDA_SECTOR_TYPES = new Set<SectorType>([
    SectorType.WEEKLY_PLANNER,
    SectorType.SPA,
    SectorType.RESTAURANT,
    SectorType.OTHER,
]);

const getErrorMessage = (error: unknown) => {
    if (!error) return undefined;
    if (error instanceof Error) return error.message;
    return "Unable to load agenda print preview data.";
};

const uniqueIds = (ids: Array<string | undefined>) => Array.from(new Set(
    ids.filter((id): id is string => Boolean(id)),
));

const toDateValue = (value: unknown): Date | undefined => {
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? undefined : value;
    if (typeof value !== "string" && typeof value !== "number") return undefined;

    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? undefined : date;
};

const getReservationStart = (reservation: Reservation) => (
    toDateValue(reservation.expectedStartDate) ?? toDateValue(reservation.actualStartDate)
);

const getReservationEnd = (reservation: Reservation) => (
    toDateValue(reservation.expectedEndDate) ?? toDateValue(reservation.actualEndDate)
);

export const getAgendaRange = (
    profile?: Profile,
    startParam?: string | null,
    endParam?: string | null,
): {rangeStart: Date; rangeEnd: Date} => {
    const stay = profile?.currentStay ?? profile?.activeStays?.[0];
    const start = toDateValue(startParam ?? undefined)
        ?? convertLocalDateToDate(stay?.startDay)
        ?? new Date();
    const end = toDateValue(endParam ?? undefined)
        ?? convertLocalDateToDate(stay?.endDay)
        ?? start;

    return {
        rangeStart: startOfDay(start),
        rangeEnd: endOfDay(end < start ? start : end),
    };
};

// roomID is not guaranteed to appear in resourceIds.
const getReservationResourceIds = (reservation?: Reservation) => uniqueIds([
    ...(reservation?.resourceIds ?? []),
    ...(reservation?.resourceAdditionalInfo ?? []).map(info => info.roomID),
]);

interface AgendaRelatedData {
    ageCategories: AgeCategory[];
    accommodationReservation?: Reservation;
    accommodationResources: Resource[];
    accommodationSector?: Sector;
    agendaItems: AgendaPrintItem[];
    reservations: Reservation[];
}

const isAgendaReservation = (
    reservation: Reservation,
    sectorsById: Map<string, Sector>,
) => {
    const sector = reservation.sectorId ? sectorsById.get(reservation.sectorId) : undefined;

    return Boolean(sector?.sectorType && AGENDA_SECTOR_TYPES.has(sector.sectorType));
};

const buildAgendaItems = (
    reservations: Reservation[],
    servicesById: Map<string, Service>,
    sectorsById: Map<string, Sector>,
    resourcesById: Map<string, Resource>,
): AgendaPrintItem[] => (
    reservations
        .map((reservation): AgendaPrintItem | null => {
            const sector = reservation.sectorId ? sectorsById.get(reservation.sectorId) : undefined;
            if (!sector?.sectorType || !AGENDA_SECTOR_TYPES.has(sector.sectorType)) return null;

            const service = reservation.serviceId ? servicesById.get(reservation.serviceId) : undefined;
            const resources = getReservationResourceIds(reservation)
                .map(resourceId => resourcesById.get(resourceId))
                .filter((resource): resource is Resource => Boolean(resource));

            return {
                end: getReservationEnd(reservation),
                id: reservation.id,
                reservation: reservation as unknown as ResolveRecord,
                resources: resources as unknown as ResolveRecord[],
                sector: sector as unknown as ResolveRecord,
                sectorType: sector.sectorType,
                service: service as unknown as ResolveRecord | undefined,
                start: getReservationStart(reservation),
            };
        })
        .filter((item): item is AgendaPrintItem => Boolean(item))
        .sort((left, right) => (left.start?.getTime() ?? 0) - (right.start?.getTime() ?? 0))
);

const getRelatedAgendaPrintData = async (
    profileId: string,
    rangeStart: Date,
    rangeEnd: Date,
): Promise<AgendaRelatedData> => {
    const [reservationsPage, sectorsPage, ageCategories] = await Promise.all([
        ReservationService.getAllReservations({
            profileIds: [profileId],
            status: AGENDA_STATUSES,
            overlaps: {start: rangeStart, end: rangeEnd},
            limit: RESERVATION_LIMIT,
        }),
        SectorService.getAllSectors({limit: LIST_LIMIT}),
        AgeCategoryService.getAllAgeCategories({limit: LIST_LIMIT})
            .then(page => page.content ?? [])
            .catch(() => [] as AgeCategory[]),
    ]);
    const reservations = reservationsPage.content ?? [];
    const sectors = sectorsPage.content ?? [];
    const sectorsById = new Map(sectors
        .filter(sector => Boolean(sector.id))
        .map(sector => [sector.id!, sector]));

    const serviceIds = uniqueIds(reservations.map(reservation => reservation.serviceId));
    const services = serviceIds.length
        ? await ServiceService.getAllServices({
            id: serviceIds,
            limit: LIST_LIMIT,
        })
            .then(page => page.content ?? [])
            .catch(() => [] as Service[])
        : [];
    const servicesById = new Map(services
        .filter(service => Boolean(service.id))
        .map(service => [service.id!, service]));

    // Earliest overlapping accommodation reservation drives stay/header data.
    const accommodationReservations = reservations
        .filter(reservation => {
            const sector = reservation.sectorId ? sectorsById.get(reservation.sectorId) : undefined;
            return sector?.sectorType === SectorType.ACCOMMODATION;
        })
        .sort((left, right) => (
            (getReservationStart(left)?.getTime() ?? 0) - (getReservationStart(right)?.getTime() ?? 0)
        ));
    const accommodationReservation = accommodationReservations[0];
    const accommodationSector = accommodationReservation?.sectorId
        ? sectorsById.get(accommodationReservation.sectorId)
        : undefined;
    const accommodationResourceIds = getReservationResourceIds(accommodationReservation);

    const resourceIds = uniqueIds([
        ...accommodationResourceIds,
        ...reservations
            .filter(reservation => isAgendaReservation(reservation, sectorsById))
            .flatMap(reservation => getReservationResourceIds(reservation)),
    ]);
    const resources = resourceIds.length
        ? await ResourceService.getAllResources({
            id: resourceIds,
            limit: LIST_LIMIT,
        }).then(page => page.content ?? []).catch(() => [] as Resource[])
        : [];
    const resourcesById = new Map(resources
        .filter(resource => Boolean(resource.id))
        .map(resource => [resource.id!, resource]));
    const accommodationResourceIdSet = new Set(accommodationResourceIds);

    return {
        ageCategories,
        accommodationReservation,
        // Header context receives stay resources only; activity rooms must not leak into room names.
        accommodationResources: resources.filter(resource => (
            Boolean(resource.id) && accommodationResourceIdSet.has(resource.id!)
        )),
        accommodationSector,
        agendaItems: buildAgendaItems(reservations, servicesById, sectorsById, resourcesById),
        reservations,
    };
};

const getAgendaPrintoutScore = (printout: Printout, profile?: Profile) => {
    let score = 1;
    if (printoutHasLanguage(printout, profile?.languageCode)) score += 100;

    return score;
};

export const selectAgendaPrintout = (
    printouts: Printout[],
    profile?: Profile,
) => (
    [...printouts].sort((left, right) => {
        const scoreDifference = getAgendaPrintoutScore(right, profile) - getAgendaPrintoutScore(left, profile);
        if (scoreDifference !== 0) return scoreDifference;
        return (left.name ?? "").localeCompare(right.name ?? "");
    })[0]
);

export const useAgendaPrintData = (
    profileId?: string,
    selectedPrintoutId?: string,
    startParam?: string | null,
    endParam?: string | null,
    requestedLanguage?: string,
): AgendaPrintData => {
    const organization = useOrganization();
    const user = useSelector<any, User | undefined>(state => state.auth.user);

    const profileQuery = useQuery(
        ["printV3", "agendaProfile", profileId],
        () => ProfileService.getProfileById(profileId!, true),
        {
            enabled: Boolean(profileId),
            refetchOnWindowFocus: false,
        },
    );

    const {rangeStart, rangeEnd} = useMemo(
        () => getAgendaRange(profileQuery.data, startParam, endParam),
        [profileQuery.data, startParam, endParam],
    );

    const relatedDataQuery = useQuery(
        ["printV3", "agendaRelatedData", profileQuery.data?.id, rangeStart.getTime(), rangeEnd.getTime()],
        () => getRelatedAgendaPrintData(profileQuery.data!.id!, rangeStart, rangeEnd),
        {
            enabled: Boolean(profileQuery.data?.id),
            refetchOnWindowFocus: false,
        },
    );

    const printoutQuery = useQuery(
        ["printV3", "agendaPrintouts"],
        () => PrintoutService.getAllPrintouts({
            archived: false,
            limit: PRINTOUT_LIST_LIMIT,
        }).then(page => (
            page.content?.filter(printout => (
                printout.type === PrintoutType.AGENDA_HANDOUT && printoutHasLayouts(printout)
            )) ?? []
        )),
        {refetchOnWindowFocus: false},
    );

    const templateQuery = useQuery(
        ["printV3", "printoutTemplates"],
        () => PrintoutService.getPrintoutTemplates(),
        {refetchOnWindowFocus: false},
    );

    /*
        The backend's own draft for the built-in handout, carrying every language the template
        ships - the document shown is still picked per guest language further down.
    */
    const fallbackPrintout = useMemo(() => {
        const draft = templateQuery.data?.find(entry => entry.id === "agenda-handout")?.draft;
        if (!draft) return undefined;

        return {...draft, id: "built-in-agenda-handout"};
    }, [templateQuery.data]);

    // Saved printouts take precedence over the starter template.
    const printouts = useMemo(
        () => printoutQuery.data?.length
            ? printoutQuery.data
            : fallbackPrintout ? [fallbackPrintout] : [],
        [fallbackPrintout, printoutQuery.data],
    );

    const selectedPrintout = useMemo(
        () => (
            printouts.find(printout => printout.id === selectedPrintoutId)
            ?? selectAgendaPrintout(printouts, profileQuery.data)
        ),
        [printouts, profileQuery.data, selectedPrintoutId],
    );

    const language = selectedPrintout
        ? resolvePrintoutLanguage(
            selectedPrintout,
            requestedLanguage ?? profileQuery.data?.languageCode,
        )
        : undefined;

    const context = useMemo<PrintoutVariableResolveContext | undefined>(() => {
        if (!profileQuery.data || !selectedPrintout || !language) return undefined;

        const relatedData = relatedDataQuery.data;

        return {
            ageCategories: relatedData?.ageCategories as unknown as ResolveRecord[] | undefined,
            agendaEnd: rangeEnd,
            agendaItems: (relatedData?.agendaItems ?? []) as unknown as ResolveRecord[],
            agendaStart: rangeStart,
            language,
            now: new Date(),
            organization: organization as unknown as ResolveRecord | undefined,
            profile: profileQuery.data as unknown as ResolveRecord,
            reservation: relatedData?.accommodationReservation as unknown as ResolveRecord | undefined,
            resources: (relatedData?.accommodationResources ?? []) as unknown as ResolveRecord[],
            sector: relatedData?.accommodationSector as unknown as ResolveRecord | undefined,
            user: user as unknown as ResolveRecord | undefined,
        };
    }, [
        organization,
        language,
        profileQuery.data,
        rangeEnd,
        rangeStart,
        relatedDataQuery.data,
        selectedPrintout,
        user,
    ]);

    const errorMessage = getErrorMessage(profileQuery.error)
        ?? getErrorMessage(relatedDataQuery.error)
        ?? getErrorMessage(printoutQuery.error)
        ?? getErrorMessage(templateQuery.error);

    return {
        agendaItems: relatedDataQuery.data?.agendaItems ?? [],
        context,
        errorMessage,
        isLoading: profileQuery.isLoading
            || relatedDataQuery.isLoading
            || printoutQuery.isLoading
            || templateQuery.isLoading,
        language,
        profile: profileQuery.data,
        rangeStart,
        rangeEnd,
        accommodationReservation: relatedDataQuery.data?.accommodationReservation,
        selectedPrintout,
        printouts,
    };
};
